import React from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import Snackbar from '~/components/Snackbar';
import useTeams from '~/data/hooks/useTeams';
import {
  MODULE_INSTANCE,
  UPDATE_COLLAGES_SETUP,
  UPDATE_MODULE_INSTANCE,
} from '~/data/operations/yearbook';
import { YearbookStackParamList } from '~/navigation/types';
import { t } from '~/utils/i18n';
import {
  ModuleSetupLayoutLoading,
  UpdateModuleInstanceInput,
} from '../layout/ModuleSetupLayout';
import CollagesSetupLayout, { UpdateCollagesSetupInput } from './layout';

type CollagesSetupNavProp = StackNavigationProp<
  YearbookStackParamList,
  'CollagesSetup'
>;
type CollagesSetupRouteProp = RouteProp<
  YearbookStackParamList,
  'CollagesSetup'
>;

export default function CollagesSetup(): JSX.Element {
  const navigation = useNavigation<CollagesSetupNavProp>();
  const {
    params: { moduleInstanceId },
  } = useRoute<CollagesSetupRouteProp>();

  const { data: moduleInstanceData, loading: moduleInstanceLoading } = useQuery(
    MODULE_INSTANCE,
    {
      variables: {
        id: moduleInstanceId,
      },
    },
  );

  const {
    teams,
    loading: teamsLoading,
    onLoadMore: onLoadMoreTeams,
  } = useTeams();

  const moduleInstance = moduleInstanceData?.moduleInstance;

  const collagesSetupConfig = moduleInstanceData?.moduleInstance?.collagesSetup;

  const [updateModuleInstance] = useMutation(UPDATE_MODULE_INSTANCE);
  const [updateCollagesSetup] = useMutation(UPDATE_COLLAGES_SETUP);

  const onUpdateModuleInstance = async ({
    isActive,
    dueDate,
  }: UpdateModuleInstanceInput) => {
    try {
      await updateModuleInstance({
        variables: {
          input: {
            id: moduleInstanceId,
            isActive,
            dueDate,
          },
        },
      });
    } catch (e) {
      if (e instanceof Error) {
        Snackbar.show(e.message);
      }
    }
  };

  const onUpdateCollagesSetup = async ({
    createAlbumsTeamId,
    addPhotosTeamId,
    viewPhotosTeamId,
  }: UpdateCollagesSetupInput) => {
    try {
      if (moduleInstance && collagesSetupConfig) {
        await updateCollagesSetup({
          variables: {
            input: {
              id: collagesSetupConfig.id,
              isActive: moduleInstance.isActive,
              dueDate: moduleInstance.dueDate,
              createAlbumsTeam: createAlbumsTeamId,
              addPhotosTeam: addPhotosTeamId,
              viewPhotosTeam: viewPhotosTeamId,
            },
          },
        });
      }
    } catch (e) {
      if (e instanceof Error) {
        Snackbar.show(e.message);
      }
    }
  };

  if (
    !moduleInstance ||
    !collagesSetupConfig ||
    moduleInstanceLoading ||
    teamsLoading
  ) {
    return (
      <ModuleSetupLayoutLoading
        title={t('screens.collagesSetup')}
        onBack={() => navigation.goBack()}
      />
    );
  }

  return (
    <CollagesSetupLayout
      moduleInstance={moduleInstance}
      collagesSetupConfig={collagesSetupConfig}
      teams={teams}
      onBack={() => navigation.goBack()}
      onUpdateModuleInstance={onUpdateModuleInstance}
      onUpdateCollagesSetup={onUpdateCollagesSetup}
      onLoadMoreTeams={onLoadMoreTeams}
    />
  );
}
